import { useState } from "react";
import { pluralizeWithCount } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { HStack } from "@design-system/HStack/HStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    REMIND_BATCH_MAX,
    useRemindOrgIntegrationConnectionsMutation,
} from "@api/organizationIntegration.api";
import { ClientAssignmentModal } from "./ClientAssignmentModal";
import { EntityAssignmentModal } from "./EntityAssignmentModal";
import { SelectedAssignmentTargets } from "./selectedAssignmentTargets";
import { SelectedReminderTargets } from "./selectedReminderTargets";
import { assignmentCapReason } from "./useCommitAssignments";
import { useOrgIntegrationBulkCommandActions } from "./useOrgIntegrationBulkCommandActions";

interface SendBlockedReasonParams {
    hasNothingToSend: boolean;
    isOverLimit: boolean;
}

const sendBlockedReason = ({
    hasNothingToSend,
    isOverLimit,
}: SendBlockedReasonParams): string | undefined => {
    if (hasNothingToSend) {
        return "None of the selected accounts are disconnected accounts someone else connected.";
    }
    if (isOverLimit) {
        return `Send to at most ${REMIND_BATCH_MAX} logins at a time.`;
    }
    return undefined;
};

/** Which field the modal was opened to change, when it is open. */
type AssignmentScope = "client" | "entity";

interface IntegrationsBulkActionsFooterProps {
    organizationId: string;
    selectedCount: number;
    targets: SelectedReminderTargets;
    assignmentTargets: SelectedAssignmentTargets;
    onClearSelection: () => void;
    /** Drops just the rows that were successfully reminded. */
    onRemindersSent: (connectionIds: string[]) => void;
    /** Drops just the rows that were successfully assigned. */
    onAccountsAssigned: (accountIds: number[]) => void;
}

/**
 * Bulk action bar for the org integrations table.
 *
 * "Send email (N)" counts eligible *accounts* while the request goes out per
 * *login*, so the toast reports people rather than rows — five accounts under
 * one Chase login is one nudge, and saying "5" there would be a lie.
 */
export const IntegrationsBulkActionsFooter = ({
    organizationId,
    selectedCount,
    targets,
    assignmentTargets,
    onClearSelection,
    onRemindersSent,
    onAccountsAssigned,
}: IntegrationsBulkActionsFooterProps) => {
    const { error: errorToast, success: successToast } = useToaster();
    const remindMutation =
        useRemindOrgIntegrationConnectionsMutation(organizationId);
    const [assignmentScope, setAssignmentScope] =
        useState<AssignmentScope | null>(null);

    const { eligibleAccountCount, connectionIds } = targets;
    const isOverLimit = connectionIds.length > REMIND_BATCH_MAX;
    const hasNothingToSend = eligibleAccountCount === 0;

    const {
        entityAccountIds,
        clientAccountIds,
        lockedWorkspaceId,
        sharedEntityId,
    } = assignmentTargets;
    const clientBlockedReason =
        assignmentTargets.clientBlockedReason ??
        assignmentCapReason(clientAccountIds.length);
    const entityBlockedReason =
        assignmentTargets.blockedReason ??
        assignmentCapReason(entityAccountIds.length);

    const handleSend = () => {
        if (hasNothingToSend || isOverLimit) {
            return;
        }
        remindMutation.mutate(connectionIds, {
            onSuccess: ({ sent, failed }) => {
                if (sent.length > 0) {
                    successToast(
                        `Reminder sent to ${pluralizeWithCount("recipient", sent.length)}`,
                    );
                    onRemindersSent(sent.map((item) => item.connectionId));
                }

                if (failed.length > 0) {
                    const reasons = [
                        ...new Set(failed.map((failure) => failure.error)),
                    ].join(" ");
                    errorToast(
                        `Couldn't remind ${pluralizeWithCount("connection", failed.length)}. ${reasons}`,
                    );
                }
            },
            // A rejected request is already toasted by the shared axios
            // interceptor, and the selection is left intact so the user can
            // retry without re-picking every row.
        });
    };

    const openAssignment = (scope: AssignmentScope) => () => {
        const blockedReason =
            scope === "client" ? clientBlockedReason : entityBlockedReason;
        if (blockedReason !== undefined) {
            return;
        }
        setAssignmentScope(scope);
    };

    // Registered from here rather than the page: this bar is mounted exactly
    // while a selection exists, which is when ⌘K should be overridden.
    useOrgIntegrationBulkCommandActions({
        selectedCount,
        eligibleAccountCount,
        eligibleClientCount: clientAccountIds.length,
        eligibleEntityCount: entityAccountIds.length,
        onSendEmail: handleSend,
        onClearSelection,
        onUpdateClient:
            clientBlockedReason === undefined
                ? openAssignment("client")
                : undefined,
        onUpdateEntity:
            entityBlockedReason === undefined
                ? openAssignment("entity")
                : undefined,
    });

    const blockedReason = sendBlockedReason({ hasNothingToSend, isOverLimit });

    return (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8} align="center">
                <BulkSelectionControl
                    selectedCount={selectedCount}
                    onClearSelection={onClearSelection}
                    size="md"
                />
            </HStack>
            <HStack gap={8} align="center">
                <Tooltip key={blockedReason} content={blockedReason}>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSend}
                        disabled={
                            remindMutation.isPending ||
                            hasNothingToSend ||
                            isOverLimit
                        }
                    >
                        {remindMutation.isPending
                            ? "Sending..."
                            : `Send email (${eligibleAccountCount})`}
                    </Button>
                </Tooltip>
                <Tooltip
                    key={clientBlockedReason}
                    content={clientBlockedReason}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={openAssignment("client")}
                        disabled={clientBlockedReason !== undefined}
                    >
                        Update client ({clientAccountIds.length})
                    </Button>
                </Tooltip>
                <Tooltip
                    key={entityBlockedReason}
                    content={entityBlockedReason}
                >
                    <Button
                        variant="primary"
                        size="md"
                        onClick={openAssignment("entity")}
                        disabled={entityBlockedReason !== undefined}
                    >
                        Update entity ({entityAccountIds.length})
                    </Button>
                </Tooltip>
            </HStack>
            {assignmentScope === "client" && (
                // Picking the client hands over to the entity step itself, so
                // a bulk client update lands the same assignment a row does.
                <ClientAssignmentModal
                    accountIds={clientAccountIds}
                    initialWorkspaceId={lockedWorkspaceId}
                    onClose={() => setAssignmentScope(null)}
                    onAssigned={onAccountsAssigned}
                />
            )}
            {assignmentScope === "entity" && lockedWorkspaceId !== null && (
                <EntityAssignmentModal
                    accountIds={entityAccountIds}
                    workspaceId={lockedWorkspaceId}
                    initialEntityId={sharedEntityId}
                    onClose={() => setAssignmentScope(null)}
                    onAssigned={onAccountsAssigned}
                />
            )}
        </CommandIsland>
    );
};
